import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../hooks/useAuth';
import { TokenService } from '../../utils/TokenService';

/**
 * ReviewerCard — the purple "ผู้โพสต์ / ผู้สร้างสรรค์" info box.
 *
 * Single source of truth for this card. Used in three places:
 *   1. header of the selected reviewer (ผู้รีวิว tab)
 *   2. each row of the reviewer search-results list
 *   3. anywhere else a reviewer needs to be shown with a ติดตาม button
 */

const PLACEHOLDER_NAMES = ['ผู้โพสต์', 'Poster', '发布者'];

export const getReviewerDisplayName = (reviewer, language) => {
  const raw = reviewer?.name;
  if (raw && !PLACEHOLDER_NAMES.includes(raw)) return raw;
  return language === 'TH' ? 'ผู้ใช้' : language === 'CN' ? '用户' : 'User';
};

export const getReviewerInitials = (reviewer) => {
  const raw = reviewer?.name;
  if (raw && !PLACEHOLDER_NAMES.includes(raw)) {
    return raw.substring(0, 2).toUpperCase();
  }
  return 'US';
};

const ReviewerCard = ({
  reviewer,               // { id, name, avatar_url, bio, review_count, item_count }
  language = 'TH',
  itemCount = null,       // overrides reviewer.item_count (e.g. live list length)
  onClose = null,         // shows the ✕ button when provided
  onClick = null,         // makes the whole card clickable (search results list)
  showFollow = true,
  dense = false,
  sx = {},
}) => {
  const { isAuthenticated } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const reviewerId = reviewer?.id ?? null;

  useEffect(() => {
    if (!isAuthenticated || !reviewerId) {
      setIsFollowing(false);
      return;
    }

    let cancelled = false;
    const fetchFollowStatus = async () => {
      try {
        const activeToken = TokenService.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${reviewerId}/follow-status`, {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setIsFollowing(!!data.following);
        }
      } catch (err) {
        console.warn('Error fetching follow status:', err);
      }
    };

    fetchFollowStatus();
    return () => { cancelled = true; };
  }, [isAuthenticated, reviewerId]);

  const handleToggleFollow = useCallback(async (e) => {
    if (e) e.stopPropagation();

    if (!isAuthenticated) {
      alert(
        language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อติดตามผู้สร้างสรรค์'
          : language === 'CN' ? '请登录以关注创建者'
            : 'Please log in to follow creators'
      );
      return;
    }
    if (followLoading || !reviewerId) return;

    setFollowLoading(true);
    try {
      const activeToken = TokenService.getAccessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${reviewerId}/toggle-follow`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setIsFollowing(!!data.following);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowLoading(false);
    }
  }, [isAuthenticated, followLoading, reviewerId, language]);

  if (!reviewer) return null;

  const displayName = getReviewerDisplayName(reviewer, language);
  const initials = getReviewerInitials(reviewer);
  const count = itemCount !== null && itemCount !== undefined
    ? itemCount
    : (reviewer.item_count ?? reviewer.review_count ?? 0);

  const avatarSize = dense ? 38 : 44;

  return (
    <Box
      onClick={onClick || undefined}
      sx={{
        p: dense ? 1 : 1.5,
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(109, 40, 217, 0.03) 100%)',
        border: '1px solid rgba(124, 58, 237, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        position: 'relative',
        boxShadow: '0 4px 20px rgba(124, 58, 237, 0.06)',
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        ...(onClick ? {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 24px rgba(124, 58, 237, 0.14)',
          },
        } : {}),
        ...sx,
      }}
    >
      {/* Avatar */}
      <Box sx={{
        width: avatarSize,
        height: avatarSize,
        borderRadius: '50%',
        backgroundColor: '#7c3aed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 800,
        fontSize: initials.length > 1 ? 16 : 20,
        boxShadow: '0 4px 10px rgba(124, 58, 237, 0.3)',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {reviewer.avatar_url ? (
          <img
            src={reviewer.avatar_url}
            alt={displayName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : initials}
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.68rem',
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          {language === 'TH' ? 'ผู้โพสต์ / ผู้สร้างสรรค์' : language === 'CN' ? '发布者 / 创建者' : 'CREATOR / POSTER'}
        </Typography>

        <Typography sx={{
          fontSize: '0.95rem',
          fontWeight: 800,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {displayName}
        </Typography>

        {reviewer.bio && (
          <Typography sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mt: 0.2,
            fontStyle: 'italic',
          }}>
            "{reviewer.bio}"
          </Typography>
        )}

        <Typography sx={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 650, mt: 0.2 }}>
          📍 {count} {language === 'TH' ? 'รายการน่าสนใจ' : language === 'CN' ? '个推荐项目' : 'interesting items'}
        </Typography>
      </Box>

      {/* Follow button */}
      {showFollow && reviewerId && (
        <Button
          variant={isFollowing ? 'outlined' : 'contained'}
          size="small"
          onClick={handleToggleFollow}
          disabled={followLoading}
          sx={{
            borderRadius: '20px',
            fontSize: '0.72rem',
            textTransform: 'none',
            fontWeight: 700,
            py: 0.5,
            px: 2,
            mr: onClose ? 0.5 : 0,
            flexShrink: 0,
            ...(isFollowing ? {
              borderColor: '#7c3aed',
              color: '#7c3aed',
              '&:hover': {
                borderColor: '#6d28d9',
                backgroundColor: 'rgba(124, 58, 237, 0.04)',
              },
            } : {
              backgroundColor: '#7c3aed',
              color: '#ffffff',
              boxShadow: '0 4px 10px rgba(124, 58, 237, 0.25)',
              '&:hover': {
                backgroundColor: '#6d28d9',
                boxShadow: '0 6px 14px rgba(124, 58, 237, 0.35)',
              },
            }),
          }}
        >
          {followLoading ? '...' : (isFollowing
            ? (language === 'TH' ? 'ติดตามแล้ว' : language === 'CN' ? '已关注' : 'Following')
            : (language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Follow')
          )}
        </Button>
      )}

      {/* Close / clear selection */}
      {onClose && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              color: 'error.main',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default ReviewerCard;
